import {
    Box,
    Checkbox,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Tooltip,
    Typography,
} from '@mui/material';

import {
    DataGrid,
    GridCellParams,
    gridClasses,
    GridColDef,
} from '@mui/x-data-grid';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useEffect, useState } from 'react';

import {
    getMemberClaims,
    getMemberLast10Visits,
    getStatusColor,
    getVisitType,
} from '../../../../store/performanceDashboard.slice';

import { localDateFormat } from '../../../../utils/timeFormat';
import { formatDate } from '../../../../utils/dateHelpers';
import { isDefaultDate } from '../../../../utils/common';

import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { RootState } from '../../../../reducers';

const NumberOfVisits = (props) => {
    const dispatch = useAppDispatch();

    const {
        memberClaims,
        last10Visits,
    } = useSelector(
        (state: RootState) =>
            state.performanceDashboard
    );

    const [openTooltip, setOpenTooltip] =
        useState<string | null>(null);

    const [selectedPeriods, setSelectedPeriods] =
        useState<string[]>([]);

    const formatRangeDate = (
        date?: string | Date | null
    ) => {
        if (!date)
            return '-';

        return formatDate(
            new Date(date),
            'MM/dd/yyyy'
        );
    };

    const formatVisitValue = (
        value: number | null | undefined
    ) => {
        if (
            value === null ||
            value === undefined
        ) {
            return '-';
        }

        return value;
    };

    const getLastYearDate = (
        date?: string | Date | null
    ) => {
        if (!date)
            return null;

        const result =
            new Date(date);

        result.setFullYear(
            result.getFullYear() - 1
        );

        return result;
    };

    const getQuarterLabel = (
        date?: string | Date | null
    ) => {
        if (!date)
            return '';

        const value =
            new Date(date);

        const quarter =
            Math.floor(
                value.getMonth() / 3
            ) + 1;

        return `Q${quarter} ${value.getFullYear()}`;
    };

    const lastQuarterLabel =
        getQuarterLabel(
            memberClaims.LastQuarterStartDate
        );

    const lastQuarterLastYearLabel =
        getQuarterLabel(
            getLastYearDate(
                memberClaims.LastQuarterStartDate
            )
        );

    const chpMedianLabel =
        lastQuarterLabel
            ? `CHP Median (${lastQuarterLabel})`
            : 'CHP Median';

    const getPerformanceDisplayName = (
        name?: string | null
    ) => {
        if (!name)
            return '';

        if (name === 'Baseline')
            return name;

        return name.replace(
            'Performance ',
            'Performance Year '
        );
    };

    const getPerformancePeriod = (
        key: string
    ) => {
        if (!key)
            return undefined;

        return memberClaims
            .PerformancePeriods
            ?.find(
                (period) =>
                    period.Key === key
            );
    };

    useEffect(() => {
        if (
            selectedPeriods.length === 0 &&
            memberClaims
                .PerformancePeriods
                ?.length > 0 &&
            memberClaims
                .CurrentPerformanceKey
        ) {
            setSelectedPeriods([
                'Baseline',
                memberClaims
                    .CurrentPerformanceKey,
            ]);
        }
    }, [
        memberClaims.PerformancePeriods,
        memberClaims.CurrentPerformanceKey,
        selectedPeriods.length,
    ]);

    const handlePeriodChange = (
        event:
            SelectChangeEvent<string[]>
    ) => {
        const value =
            event.target.value;

        const values =
            typeof value === 'string'
                ? value.split(',')
                : value;

        if (values.length === 0)
            return;

        if (values.length <= 2)
            setSelectedPeriods(values);
    };

    const getPerformanceNumber = (
        key: string
    ) => {
        if (
            !key ||
            key === 'Baseline'
        ) {
            return 0;
        }

        return (
            Number(
                key.replace(
                    'Performance',
                    ''
                )
            ) || 0
        );
    };

    const orderedSelectedPeriods = [
        ...selectedPeriods,
    ].sort(
        (first, second) =>
            getPerformanceNumber(second) -
            getPerformanceNumber(first)
    );

    const selectedPeriodOne =
        getPerformancePeriod(
            orderedSelectedPeriods[0]
        );

    const selectedPeriodTwo =
        getPerformancePeriod(
            orderedSelectedPeriods[1]
        );

    const getLatestSelectedPeriodKey =
        () => {
            if (
                selectedPeriods.length <
                2
            ) {
                return '';
            }

            return orderedSelectedPeriods[0];
        };

    const getPerformanceCellClass = (
        periodKey: string,
        row: any
    ) => {
        if (
            !periodKey ||
            selectedPeriods.length < 2
        ) {
            return 'black';
        }

        const latestSelectedPeriodKey =
            getLatestSelectedPeriodKey();

        if (
            !latestSelectedPeriodKey ||
            periodKey !==
                latestSelectedPeriodKey ||
            periodKey === 'Baseline'
        ) {
            return 'black';
        }

        const comparePeriodKey =
            orderedSelectedPeriods.find(
                (key) =>
                    key !== periodKey
            );

        if (!comparePeriodKey)
            return 'black';

        const latestValue =
            row.PerformanceValues?.[
                periodKey
            ];

        const compareValue =
            row.PerformanceValues?.[
                comparePeriodKey
            ];

        if (
            latestValue === null ||
            latestValue === undefined ||
            compareValue === null ||
            compareValue === undefined
        ) {
            return 'black';
        }

        return getStatusColor(
            latestValue,
            compareValue
        );
    };

    const getQuarterCellClass = (
        row: any
    ) => {
        const currentValue =
            row.LastQuarter;

        const compareValue =
            row.LastQuarterLastYear;

        if (
            currentValue === null ||
            currentValue === undefined ||
            compareValue === null ||
            compareValue === undefined
        ) {
            return 'black';
        }

        return getStatusColor(
            currentValue,
            compareValue
        );
    };

    const renderHeaderWithInfo = (
        key: string,
        label: string,
        tooltipText: string
    ) => (
        <Box
            display="flex"
            alignItems="center"
            gap={0.25}
        >
            <span>
                {label}
            </span>

            <Tooltip
                title={tooltipText}
                open={
                    openTooltip === key
                }
                onClose={() =>
                    setOpenTooltip(null)
                }
                disableHoverListener
                disableFocusListener
                disableTouchListener
                arrow
            >
                <InfoOutlinedIcon
                    sx={{
                        fontSize: 15,
                        cursor: 'pointer',
                        flexShrink: 0,
                    }}
                    onClick={(event) => {
                        event.stopPropagation();

                        setOpenTooltip(
                            openTooltip === key
                                ? null
                                : key
                        );
                    }}
                />
            </Tooltip>
        </Box>
    );

    /*
     * QUARTERLY COMPARISON
     */
    const quarterColumns:
        GridColDef[] = [
        {
            field: 'Name',
            headerName: '',
            flex: 1.2,
            minWidth: 130,
            sortable: false,
        },
        {
            field: 'LastQuarter',

            headerName:
                lastQuarterLabel ||
                'Last Qtr',

            flex: 1,
            minWidth: 110,
            sortable: false,

            renderHeader: () =>
                renderHeaderWithInfo(
                    'last-quarter',

                    lastQuarterLabel ||
                        'Last Qtr',

                    `${formatRangeDate(
                        memberClaims
                            .LastQuarterStartDate
                    )} - ${formatRangeDate(
                        memberClaims
                            .LastQuarterEndDate
                    )}`
                ),

            cellClassName: (
                params:
                    GridCellParams
            ) =>
                getQuarterCellClass(
                    params.row
                ),

            valueGetter: (
                value,
                row
            ) =>
                formatVisitValue(
                    row.LastQuarter
                ),
        },
        {
            field:
                'LastQuarterLastYear',

            headerName:
                lastQuarterLastYearLabel ||
                'Last Qtr (LY)',

            flex: 1,
            minWidth: 110,
            sortable: false,

            renderHeader: () =>
                renderHeaderWithInfo(
                    'last-quarter-ly',

                    lastQuarterLastYearLabel ||
                        'Last Qtr (LY)',

                    `${formatRangeDate(
                        getLastYearDate(
                            memberClaims
                                .LastQuarterStartDate
                        )
                    )} - ${formatRangeDate(
                        getLastYearDate(
                            memberClaims
                                .LastQuarterEndDate
                        )
                    )}`
                ),

            valueGetter: (
                value,
                row
            ) =>
                formatVisitValue(
                    row.LastQuarterLastYear
                ),
        },
        {
            field:
                'LastQuarterCHPMedian',

            headerName:
                chpMedianLabel,

            flex: 1,
            minWidth: 165,
            sortable: false,

            renderHeader: () =>
                renderHeaderWithInfo(
                    'last-quarter-median',

                    chpMedianLabel,

                    `${formatRangeDate(
                        memberClaims
                            .LastQuarterStartDate
                    )} - ${formatRangeDate(
                        memberClaims
                            .LastQuarterEndDate
                    )}`
                ),

            valueGetter: (
                value,
                row
            ) =>
                formatVisitValue(
                    row.LastQuarterCHPMedian
                ),
        },
    ];

    /*
     * PERFORMANCE YEAR COMPARISON
     */
    const performanceColumns:
        GridColDef[] = [
        {
            field: 'Name',
            headerName: '',
            flex: 1.2,
            minWidth: 140,
            sortable: false,
        },
        {
            field:
                'SelectedPeriodOne',

            headerName:
                getPerformanceDisplayName(
                    selectedPeriodOne?.Name
                ) ||
                'Performance Year',

            flex: 1,
            minWidth: 150,
            sortable: false,

            renderHeader: () =>
                renderHeaderWithInfo(
                    'performance-year-one',

                    getPerformanceDisplayName(
                        selectedPeriodOne?.Name
                    ) ||
                        'Performance Year',

                    selectedPeriodOne
                        ? `${formatRangeDate(
                              selectedPeriodOne
                                  .StartDate
                          )} - ${formatRangeDate(
                              selectedPeriodOne
                                  .EndDate
                          )}`
                        : '-'
                ),

            cellClassName: (
                params:
                    GridCellParams
            ) =>
                getPerformanceCellClass(
                    orderedSelectedPeriods[0],
                    params.row
                ),

            valueGetter: (
                value,
                row
            ) =>
                orderedSelectedPeriods[0]
                    ? formatVisitValue(
                          row.PerformanceValues?.[
                              orderedSelectedPeriods[0]
                          ]
                      )
                    : '-',
        },

        ...(
            orderedSelectedPeriods.length >
            1
                ? [
                      {
                          field:
                              'SelectedPeriodTwo',

                          headerName:
                              getPerformanceDisplayName(
                                  selectedPeriodTwo
                                      ?.Name
                              ) ||
                              'Performance Year',

                          flex: 1,
                          minWidth: 150,
                          sortable: false,

                          renderHeader: () =>
                              renderHeaderWithInfo(
                                  'performance-year-two',

                                  getPerformanceDisplayName(
                                      selectedPeriodTwo
                                          ?.Name
                                  ) ||
                                      'Performance Year',

                                  selectedPeriodTwo
                                      ? `${formatRangeDate(
                                            selectedPeriodTwo
                                                .StartDate
                                        )} - ${formatRangeDate(
                                            selectedPeriodTwo
                                                .EndDate
                                        )}`
                                      : '-'
                              ),

                          cellClassName: (
                              params:
                                  GridCellParams
                          ) =>
                              getPerformanceCellClass(
                                  orderedSelectedPeriods[1],
                                  params.row
                              ),

                          valueGetter: (
                              value,
                              row
                          ) =>
                              formatVisitValue(
                                  row.PerformanceValues?.[
                                      orderedSelectedPeriods[1]
                                  ]
                              ),
                      } as GridColDef,

                      {
                          field:
                              'Difference',

                          headerName:
                              'Difference',

                          flex: 1,
                          minWidth: 110,
                          sortable: false,

                          valueGetter: (
                              value,
                              row
                          ) => {
                              const latestKey =
                                  orderedSelectedPeriods[0];

                              const compareKey =
                                  orderedSelectedPeriods[1];

                              if (
                                  !latestKey ||
                                  !compareKey
                              ) {
                                  return '-';
                              }

                              const latestValue =
                                  row.PerformanceValues?.[
                                      latestKey
                                  ];

                              const compareValue =
                                  row.PerformanceValues?.[
                                      compareKey
                                  ];

                              if (
                                  latestValue ===
                                      null ||
                                  latestValue ===
                                      undefined ||
                                  compareValue ===
                                      null ||
                                  compareValue ===
                                      undefined
                              ) {
                                  return '-';
                              }

                              return (
                                  latestValue -
                                  compareValue
                              );
                          },
                      } as GridColDef,
                  ]
                : []
        ),
    ];

    /*
     * LAST 10 VISITS
     */
    const last10VisitsColumns:
        GridColDef[] = [
        {
            field: 'ClaimDate',
            headerName: 'Date',
            flex: 1,
            sortable: false,

            valueGetter: (
                value,
                row
            ) =>
                isDefaultDate(
                    row.ClaimDate
                )
                    ? '-'
                    : localDateFormat(
                          new Date(
                              row.ClaimDate
                          )
                      ),
        },
        {
            field: 'VisitType',
            headerName: 'Visit Type',
            flex: 1,
            sortable: false,

            valueGetter: (
                value,
                row
            ) =>
                getVisitType(
                    row.VisitType
                ),
        },
        {
            field: 'Facility',
            headerName: 'Facility',
            flex: 1,
            sortable: false,

            valueGetter: (
                value,
                row
            ) =>
                row.Facility ||
                '-',
        },
    ];

    useEffect(() => {
        if (props.memberId) {
            dispatch(
                getMemberClaims(
                    props.memberId
                )
            );

            dispatch(
                getMemberLast10Visits(
                    props.memberId
                )
            );
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        props.memberId,
    ]);

    return (
        <Grid
            container
            spacing={2}
        >
            <Grid size={12}>
                <Typography
                    variant="h6"
                    color="primary"
                >
                    Number of Visits
                </Typography>
            </Grid>

            {/* Quarterly Comparison */}
            <Grid size={12}>
                <Paper
                    variant="outlined"
                    sx={{
                        p: 1.5,
                        borderRadius: 2,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        marginBottom={1}
                    >
                        Quarterly Comparison
                    </Typography>

                    <DataGrid
                        rows={
                            memberClaims
                                .MemberClaimVisit
                        }
                        columns={
                            quarterColumns
                        }
                        getRowId={(row) =>
                            row.Name
                        }
                        autoHeight
                        hideFooter
                        disableColumnMenu
                        disableRowSelectionOnClick
                        density="compact"
                        sx={{
                            border: 0,

                            '& .MuiDataGrid-columnHeader':
                                {
                                    px: 0.5,
                                },

                            [`& .${gridClasses.cell}`]:
                                {
                                    py: 0.5,
                                    px: 0.5,
                                },

                            [`& .green`]: {
                                color:
                                    'white',
                                background:
                                    'green',
                                fontWeight:
                                    600,
                            },

                            [`& .red`]: {
                                color:
                                    'white',
                                background:
                                    'red',
                                fontWeight:
                                    600,
                            },

                            [`& .black`]: {
                                color:
                                    'rgba(0,0,0,0.87)',
                                backgroundColor:
                                    'transparent',
                            },

                            '& .MuiDataGrid-columnHeaders':
                                {
                                    backgroundColor:
                                        '#fafafa',
                                },
                        }}
                    />
                </Paper>
            </Grid>

            {/* Performance Year Comparison */}
            <Grid size={12}>
                <Paper
                    variant="outlined"
                    sx={{
                        p: 1.5,
                        borderRadius: 2,
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        flexWrap="wrap"
                        marginBottom={1}
                    >
                        <Typography
                            variant="subtitle1"
                            fontWeight={600}
                        >
                            Performance Year Comparison
                        </Typography>

                        <FormControl
                            size="small"
                            sx={{
                                minWidth: 330,
                                marginBottom: 1,
                            }}
                        >
                            <InputLabel>
                                Compare Performance Year
                            </InputLabel>

                            <Select
                                multiple
                                value={
                                    selectedPeriods
                                }
                                label="Compare Performance Year"
                                onChange={
                                    handlePeriodChange
                                }
                                renderValue={() =>
                                    orderedSelectedPeriods
                                        .map(
                                            (
                                                key
                                            ) => {
                                                const period =
                                                    getPerformancePeriod(
                                                        key
                                                    );

                                                return getPerformanceDisplayName(
                                                    period
                                                        ?.Name
                                                );
                                            }
                                        )
                                        .filter(
                                            Boolean
                                        )
                                        .join(
                                            ' vs '
                                        )
                                }
                            >
                                {memberClaims
                                    .PerformancePeriods
                                    ?.map(
                                        (
                                            period
                                        ) => {
                                            const isSelected =
                                                selectedPeriods.includes(
                                                    period.Key
                                                );

                                            const isLastSelected =
                                                isSelected &&
                                                selectedPeriods.length ===
                                                    1;

                                            const isDisabledByMax =
                                                !isSelected &&
                                                selectedPeriods.length >=
                                                    2;

                                            return (
                                                <MenuItem
                                                    key={
                                                        period.Key
                                                    }
                                                    value={
                                                        period.Key
                                                    }
                                                    disabled={
                                                        isLastSelected ||
                                                        isDisabledByMax
                                                    }
                                                    sx={{
                                                        alignItems:
                                                            'flex-start',
                                                        py: 0.75,
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={
                                                            isSelected
                                                        }
                                                        disabled={
                                                            isLastSelected
                                                        }
                                                        size="small"
                                                        sx={{
                                                            pt: 0.1,
                                                            pr: 1,
                                                        }}
                                                    />

                                                    <Box
                                                        display="flex"
                                                        flexDirection="column"
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={
                                                                500
                                                            }
                                                        >
                                                            {getPerformanceDisplayName(
                                                                period.Name
                                                            )}
                                                        </Typography>

                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color:
                                                                    'text.secondary',
                                                                lineHeight:
                                                                    1.2,
                                                            }}
                                                        >
                                                            {formatRangeDate(
                                                                period.StartDate
                                                            )}

                                                            {
                                                                ' - '
                                                            }

                                                            {formatRangeDate(
                                                                period.EndDate
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            );
                                        }
                                    )}
                            </Select>
                        </FormControl>
                    </Box>

                    <DataGrid
                        key={
                            `${orderedSelectedPeriods.join(
                                '-'
                            )}-${orderedSelectedPeriods.length}`
                        }
                        rows={
                            memberClaims
                                .MemberClaimVisit
                        }
                        columns={
                            performanceColumns
                        }
                        getRowId={(row) =>
                            row.Name
                        }
                        autoHeight
                        hideFooter
                        disableColumnMenu
                        disableRowSelectionOnClick
                        density="compact"
                        sx={{
                            border: 0,

                            '& .MuiDataGrid-columnHeader':
                                {
                                    px: 0.5,
                                },

                            [`& .${gridClasses.cell}`]:
                                {
                                    py: 0.5,
                                    px: 0.5,
                                },

                            [`& .green`]: {
                                color:
                                    'white',
                                background:
                                    'green',
                                fontWeight:
                                    600,
                            },

                            [`& .red`]: {
                                color:
                                    'white',
                                background:
                                    'red',
                                fontWeight:
                                    600,
                            },

                            [`& .black`]: {
                                color:
                                    'rgba(0,0,0,0.87)',
                                backgroundColor:
                                    'transparent',
                            },

                            '& .MuiDataGrid-columnHeaders':
                                {
                                    backgroundColor:
                                        '#fafafa',
                                },
                        }}
                    />
                </Paper>
            </Grid>

            {/* Last 10 Visits */}
            <Grid size={12}>
                <Typography
                    variant="h6"
                    color="primary"
                    paddingTop={2}
                    marginBottom={1}
                >
                    Last 10 Visits
                </Typography>
            </Grid>

            <Grid
                size={12}
                sx={{
                    width: '100%',
                }}
            >
                <DataGrid
                    rows={
                        last10Visits
                    }
                    columns={
                        last10VisitsColumns
                    }
                    autoHeight
                    pageSizeOptions={[5]}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    getRowId={(row) =>
                        row.Id ??
                        `${row.ClaimDate}-${row.VisitType}-${row.Facility}`
                    }
                    sx={{
                        [`& .${gridClasses.cell}`]:
                            {
                                py: 1,
                            },
                    }}
                />
            </Grid>
        </Grid>
    );
};

export default NumberOfVisits;